// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Old is not rare. Enormous quantities of Victorian and Edwardian jewelry were made, and a great deal of it survives. A plain 1890s gold band is a hundred and thirty years old and worth roughly its metal, because there are thousands of them.

Rarity comes from something else, and it is worth learning to see, because it is the difference between paying for age and paying for something that will not come around again.

## The five things that actually make a piece scarce

**1. Survival rate.** How much of what was made still exists. Two forces destroyed antique jewelry at scale: gold price spikes, when people melted whatever they had, and fashion, which sent whole categories to the scrap merchant. Anything melted in quantity is scarce now regardless of how much was originally made.

**2. Fragility.** Pieces that broke easily are rare in good condition. Plique a jour enamel, thin filigree, articulated drops, and anything with a hinge. Our [enamel guide](/blog/enamel-jewelry-explained) covers why enamel damage cannot be undone.

**3. A short production window.** Techniques and styles that existed briefly. The genuinely fine Art Nouveau period ran roughly twenty years and much of it was made in small workshops.

**4. Labour that nobody will pay for now.** Hand cut stones fitted individually to their channels, hand engraving, granulation, fine hand piercing. Not that it cannot be done, but that the cost of doing it today exceeds what most buyers will pay, so it is not being replaced.

**5. Originality.** The single most underrated factor. A piece with its original stones, original findings, no resize and honest wear is meaningfully rarer than the same piece rebuilt, because most surviving pieces have been worked on.

## What people mistake for rarity

**Age alone.** Common Victorian gold is common.

**A high original price.** Expensive when new does not mean scarce now. Diamonds were expensive in 1900 and there are a lot of them.

**An unusual look.** Odd is not the same as rare, and some odd things were odd because they did not sell.

**A story.** "This belonged to my great-grandmother" is true of most antique jewelry and adds nothing transferable. Documented provenance to a known person is different, and genuinely valuable.

**Being unmarked.** Absence of a mark is not evidence of anything, as our [gold guide](/blog/how-to-tell-if-gold-jewelry-is-real) explains.

## Where the real scarcity sits

Some categories are genuinely thin on the ground:

| Category | Why |
| --- | --- |
| Georgian, pre-1837 | Two centuries of melting, and closed-back settings that water destroyed |
| Fine Art Nouveau | Short period, small workshops, fragile enamel |
| Men's antique gold | Melted in bulk when waistcoats went out of fashion |
| Intact matched pairs of earrings | Singles get separated, converted or lost |
| Complete boxed sets | Boxes and parts scatter |
| Untouched old cut diamonds | Many were recut to modern proportions |
| Mourning jewelry with intact hair work | Fragile, and later generations found it morbid |

Our [guide to men's pieces](/blog/antique-cufflinks-and-mens-jewelry) covers why that corner is undervalued rather than merely scarce, which is a different and more useful situation for a buyer.

## Rare and valuable are not the same thing

This is the part collectors learn late. Something can be genuinely scarce and still cheap, because scarcity only becomes price when demand meets it.

Scarce and expensive: Georgian, fine Art Nouveau, signed work by known makers, untouched old cuts.

Scarce and affordable: unfashionable categories. Victorian mourning pieces, hat pins, dress studs, watch chain fobs. Fewer survive each year and few people are competing for them.

For a collector on a budget, the second list is the interesting one. You are buying real scarcity at a price set by current fashion rather than by supply.

## How to judge a specific piece

Work through these:

1. **Could this be made today at this price?** If yes, you are paying for age, not rarity.
2. **How much of it is original?** Ask about stones, findings, shank, clasp. Our [reproduction guide](/blog/how-to-spot-fake-antique-jewelry) covers the evidence.
3. **What is the condition of the fragile part?** The enamel, the hair work, the millegrain, the hinge.
4. **How many have I seen?** Keep looking at the market for a few months before buying at the top of your budget. Your own sense of how often something appears is better than any article.
5. **Is it complete?** Both earrings, the original box, the fitted case, the T-bar on the chain.
6. **What is the floor?** Metal weight times purity gives a number below which you cannot really lose. Our [resale guide](/blog/how-much-is-my-jewelry-worth) sets out the calculation.

## Why this matters when you sell

If you are selling, rarity is the part of the price you have to argue for, and you argue for it with evidence, not adjectives.

"Rare Victorian brooch" persuades nobody. "Original C clasp and pin, all stones original, hair work intact under glass, no repairs" tells a collector exactly why this one is worth more than the other three they are looking at. Our [guide to writing a listing](/blog/how-to-write-a-jewelry-listing-that-sells) covers how to say it.

## Buying rarity at a distance

Almost all of this is judged from photographs. Ask for the back, the findings, the fragile part, and the marks, all in daylight. Ask directly what has been repaired or replaced, and treat a straight answer as a good sign rather than a warning.

On Nextmine the buyer's payment is held until the piece has arrived and been confirmed, which matters more the more unusual the piece is. [See what is listed right now](/s), or post what you are hunting for on the [ISO board](/wanted) and let sellers bring it to you.

The Hallmark Society's [Georgian guide](https://hallmarksociety.com/georgian-jewelry/) is a good illustration of all five factors at once: why so little survives, and the one thing that destroys what is left.
`;

export default body;
